import type {
  PublishedSubscriptionPlan,
  SubscriptionPlan,
} from "./types";

export const SUBSCRIPTION_PERIOD_OPTIONS = [12, 18, 24, 36, 48] as const;
export const SUBSCRIPTION_MILEAGE_OPTIONS = [500, 1000, 1500, 2000, 2500, 3000] as const;

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.replace(/\./g, "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const normalizeSubscriptionPlans = (value: unknown): SubscriptionPlan[] => {
  let raw: unknown = value;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  // O shape publicado (cms-api → OpenSearch) embrulha os planos em
  // `{ plans: [...], lowestMonthlyPrice }`; no builder/privado o valor é o array cru.
  // Aceitar os dois, senão o site publicado resolve 0 planos e o card/bloco some.
  if (
    raw &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    Array.isArray((raw as { plans?: unknown }).plans)
  ) {
    raw = (raw as { plans: unknown[] }).plans;
  }
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((item, index) => {
    if (!item || typeof item !== "object") return [];
    const plan = item as Partial<Record<keyof SubscriptionPlan, unknown>>;
    const periodMonths = toNumber(plan.periodMonths);
    const mileageKm = toNumber(plan.mileageKm);
    if (periodMonths === null || mileageKm === null) return [];

    return [
      {
        id: typeof plan.id === "string" && plan.id ? plan.id : `plan-${index}`,
        periodMonths,
        mileageKm,
        monthlyPrice: toNumber(plan.monthlyPrice),
        active: plan.active !== false,
      },
    ];
  });
};

export const isPublishedPlan = (plan: SubscriptionPlan): plan is PublishedSubscriptionPlan =>
  plan.active && typeof plan.monthlyPrice === "number" && plan.monthlyPrice > 0;

export const getPublishedPlans = (value: unknown): PublishedSubscriptionPlan[] =>
  normalizeSubscriptionPlans(value)
    .filter(isPublishedPlan)
    .sort((a, b) => a.periodMonths - b.periodMonths || a.mileageKm - b.mileageKm);

export const getLowestPlan = (value: unknown): PublishedSubscriptionPlan | undefined =>
  getPublishedPlans(value).reduce<PublishedSubscriptionPlan | undefined>(
    (lowest, plan) => (!lowest || plan.monthlyPrice < lowest.monthlyPrice ? plan : lowest),
    undefined,
  );

export const uniquePeriods = (plans: SubscriptionPlan[]): number[] =>
  [...new Set(plans.map((plan) => plan.periodMonths))].sort((a, b) => a - b);

export const uniqueMileages = (plans: SubscriptionPlan[]): number[] =>
  [...new Set(plans.map((plan) => plan.mileageKm))].sort((a, b) => a - b);

export const findPlan = (
  plans: PublishedSubscriptionPlan[],
  periodMonths: number,
  mileageKm: number,
): PublishedSubscriptionPlan | undefined =>
  plans.find((plan) => plan.periodMonths === periodMonths && plan.mileageKm === mileageKm);

export const planKey = (plan: Pick<SubscriptionPlan, "periodMonths" | "mileageKm">): string =>
  `${plan.periodMonths}x${plan.mileageKm}`;

export interface SubscriptionPlansIssues {
  duplicateIds: string[];
  incompleteIds: string[];
  empty: boolean;
}

export const findSubscriptionPlansIssues = (value: unknown): SubscriptionPlansIssues => {
  const plans = normalizeSubscriptionPlans(value);
  const seen = new Map<string, string>();
  const duplicateIds: string[] = [];

  for (const plan of plans) {
    const key = planKey(plan);
    const first = seen.get(key);
    if (first) {
      if (!duplicateIds.includes(first)) duplicateIds.push(first);
      duplicateIds.push(plan.id);
    } else {
      seen.set(key, plan.id);
    }
  }

  const incompleteIds = plans
    .filter((plan) => typeof plan.monthlyPrice !== "number" || plan.monthlyPrice <= 0)
    .map((plan) => plan.id);

  return { duplicateIds, incompleteIds, empty: !plans.some(isPublishedPlan) };
};

export const formatPeriod = (months: number): string =>
  months === 1 ? "1 mês" : `${months} meses`;

export const formatMileage = (km: number): string =>
  `${km.toLocaleString("pt-BR")} km/mês`;

export const formatMileageShort = (km: number): string =>
  `${km.toLocaleString("pt-BR")} km`;

export const formatMonthlyPrice = (price: number): string =>
  price.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
    maximumFractionDigits: 2,
  });

export const formatPlanCondition = (plan: Pick<SubscriptionPlan, "periodMonths" | "mileageKm">): string =>
  `${formatPeriod(plan.periodMonths)} · ${formatMileage(plan.mileageKm)}`;

export const formatLowestPlanSummary = (value: unknown, prefix = "A partir de"): string => {
  const lowest = getLowestPlan(value);
  if (!lowest) return "";
  return `${prefix} ${formatMonthlyPrice(lowest.monthlyPrice)}/mês`.trim();
};

export const resolveVehicleImage = (
  ...candidates: unknown[]
): { src: string; alt: string } | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return { src: candidate, alt: "" };
    if (candidate && typeof candidate === "object") {
      const image = candidate as { src?: unknown; alt?: unknown; props?: { src?: unknown; alt?: unknown } };
      const src = typeof image.src === "string" ? image.src : image.props?.src;
      if (typeof src === "string" && src.trim()) {
        const alt = typeof image.alt === "string" ? image.alt : image.props?.alt;
        return { src, alt: typeof alt === "string" ? alt : "" };
      }
    }
  }
  return undefined;
};
